'use strict';
import {fromJS, Map} from 'immutable';
import {Store} from 'client/framework';
import StudentsActions from './StudentsActions';
import {
  getAssignmentStudentStatistics
} from 'framework/MandarkAPI/Assignments/Assignments';
import AssignmentDrilldownStore from '../AssignmentDrilldownStore';
import {stats} from 'lib/StatsUtil';

/*
  TODO:
    - Eventually this will get wired up to make a request to an assignment's
      students_v1 relationship link, and render a student list fully on its own.
*/
export class StudentStore extends Store {
  constructor(name) {
    super(name);

    this.initialData = fromJS({
      assignmentId: null,
      activeStudent: null,
      classroomId: null
    });

    this.setInitialData(this.initialData);
    this.handle(StudentsActions.INITIALIZE_STUDENT_LIST, this._initializeStudentList);
    this.handle(StudentsActions.SET_ACTIVE_STUDENT, this._setActiveStudent);
  }

  _resetStore() {
    this.writeData(this.initialData);
  }

  _initializeStudentList(initialData) {
    this.writeData((state) => {
      return state
        .set('classroomId', initialData.get('classroomId'))
        .set('assignmentId', initialData.get('assignmentId'));
    });
  }

  _makeStudentStatsQuery() {
    return {
      classroomId: this.readData('classroomId'),
      assignmentId: this.readData('assignmentId')
    };
  }

  _setActiveStudent(student) {
    this.writeData('activeStudent', student);
  }

  getActiveStudent() {
    return this.readData('activeStudent');
  }

  getAssignmentId() {
    return this.readData('assignmentId');
  }

  getStudentStats() {
    return getAssignmentStudentStatistics(this._makeStudentStatsQuery());
  }

  hasStudentStats() {
    const statsQuery = this._makeStudentStatsQuery();
    if (!statsQuery.classroomId || !statsQuery.assignmentId) {
      return false;
    }
    return getAssignmentStudentStatistics(statsQuery).hasReceivedData();
  }

  /*
    Legacy code: used for retrieving data in description card at the top of the drilldown UI
  */
  _getStudentStatsInterface() {
    return this.getStudentStats().interface;
  }

  getStudentStatuses() {
    return this._getStudentStatsInterface().getStudentStatuses();
  }

  getPercentageString(num, denom) {
    return `${stats.percentage(num, denom)}%`;
  }

  /*
    This method is being used to generate a CSV file. Was moved here to avoid code duplication
    for computing the same information in the Students.react.js component and the CVS-generator.
  */
  getStudentTableData() {
    return AssignmentDrilldownStore.getActiveAssignmentStudents().reduce((students, student) => {
      const studentId = student.getId();
      const assignment = AssignmentDrilldownStore.getActiveAssignment();
      const assignmentSize = assignment.getMeta().getCountOfQuestions();
      const correctAnswers = student.getMeta().getAssignmentCountOfCorrectGuesses();
      const incorrectAnswers = student.getMeta().getAssignmentCountOfIncorrectGuesses();
      const questionsAnswered = correctAnswers + incorrectAnswers;
      const unansweredQuestions = assignmentSize - questionsAnswered;
      const totalAnswered = correctAnswers + incorrectAnswers;
      const isComplete = questionsAnswered === assignmentSize;
      return students.set(
        studentId,
        new Map({
          assignmentSize: assignmentSize,
          questionsAnswered: questionsAnswered,
          correctAnswers: correctAnswers,
          unansweredQuestions: unansweredQuestions,
          incorrectAnswers: incorrectAnswers,
          isComplete: isComplete,
          accuracy: stats.percentage(correctAnswers, questionsAnswered),
          correctPercentage: stats.percentage(correctAnswers, assignmentSize),
          incorrectPercentage: stats.percentage(incorrectAnswers, assignmentSize),
          unansweredPercentage: stats.percentage(unansweredQuestions, assignmentSize),
          totalAnswered: correctAnswers + incorrectAnswers,
          totalAnsweredPercentageString: this.getPercentageString(totalAnswered, assignmentSize),
          answeredCorrectlyPercentageString: this.getPercentageString(correctAnswers, assignmentSize)
        })
      );
    }, new Map());
  }
}

export default new StudentStore('StudentStore');
